import { useQuery } from "@tanstack/react-query";
import StudentCallView from "../calls/StudentCallView";
import SetupPreview from "../calls/SetupPreview";
import { abroadReviewApi } from "../../api/abroadLoans";
import { formatDuration, formatKigaliDate, fullName } from "../scholarships/model";
import { programmeLabel } from "./model";
import { committeeKey } from "./review";
import { rulesSummary } from "./student";

const VISIBILITY = { PUBLIC: "Public", PRIVATE: "Private" };

/**
 * An abroad loan call before it is published: its whole set-up (details, programmes with who may
 * apply, documents and criteria, the committee) and the page students will see, applying off.
 */
export default function CallPreview({ call, onClose }) {
  const { data: members = [], isPending } = useQuery({
    queryKey: committeeKey(call.id),
    queryFn: async () => (await abroadReviewApi.committee(call.id)).data,
  });

  const programmes = call.programmes ?? [];
  const meta = (p) => [p.levelName, formatDuration(p.durationMonths)].filter(Boolean).join(" · ");
  const details = [
    ["Type", "Abroad loan"],
    ["Where students study", "Where they were admitted"],
    ["Academic year", call.academicYearName],
    ["Opens", call.opensAt && formatKigaliDate(call.opensAt)],
    ["Closes", call.closesAt && `${formatKigaliDate(call.closesAt)}, 23:59 Kigali time`],
    ["Number of loans", call.awardQuota == null ? "No limit set" : String(call.awardQuota)],
    ["Visibility", VISIBILITY[call.visibility] ?? call.visibility],
    ["Public summary", call.publicSummary, true],
    ["Description", call.description, true],
  ];

  return (
    <SetupPreview
      title={call.name}
      details={details}
      programmes={programmes.map(({ programme: p, requirements, eligibility, criteria }) => ({
        id: p.id,
        name: programmeLabel(p),
        meta: meta(p),
        rules: rulesSummary(eligibility),
        requirements,
        criteria,
      }))}
      people={[{ title: "Committee", people: members.map((m) => ({ key: m.evaluatorId, label: fullName(m) })), pending: isPending, empty: "No evaluators yet." }]}
      studentPage={
        <StudentCallView
          preview
          title={call.name}
          subtitle={["Abroad loan", call.academicYearName, "study where you were admitted"].filter(Boolean).join(" · ")}
          aboutTitle="About this loan call"
          description={call.description || "No description yet."}
          programmes={programmes.map(({ programme: p, requirements, eligibility }) => ({
            id: p.id,
            name: programmeLabel(p),
            meta: meta(p),
            requirements,
            whoMayApply: (
              <p className="text-sm text-slate-700">
                <span className="font-medium">Who may apply:</span> {eligibility.length ? rulesSummary(eligibility).join("; ") : "any student"}
              </p>
            ),
          }))}
          closesAt={call.closesAt}
          open
          canStart={false}
        />
      }
      onClose={onClose}
    />
  );
}
